import { API_BASE_URL } from '@synergy/config';
import { BonusCustomerFilter, OverviewTableContainer, UrlFilter } from '@synergy/components';
import { BonusSettlementSupplierStatus, getBonusSettlementSupplierTranslationId } from '@synergy/models';
import { Box, Button, Paper, Stack } from '@mui/material';
import { DownloadIcon, SaveIcon, ThemeWrapper } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { PaloDetailView, PaloFilterModal, PaloSideRail, PaloStatusPill } from '@synergy/components-palo';
import { PoolBanner, PoolTypography } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useNavigate, useParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { BonusSettlementDetailFooter } from '../bonus-settlement-detail-footer';
import { BonusSettlementDetailFormValues } from '../../models/bonus-settlement-detail-form-values';
import { BonusSettlementDetailSubmitSuccessModal } from '../components/bonus-settlement-detail-submit-success-modal';
import { BonusSettlementDetailSumTable } from '../components/bonus-settlement-detail-sum-table';
import { BonusSettlementDetailTable } from '../bonus-settlement-detail-table';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementDetailLoadingSelector,
    bonusSettlementDetailReadOnlySelector,
    bonusSettlementDetailSelector,
} from '../store/bonus-settlements-detail-selector';
import {
    getBonusSettlementDetail,
    resetBonusSettlementDetail,
    saveBonusSettlementDetail,
    submitBonusSettlementDetail,
} from '../store/bonus-settlements-detail-actions';
import { useAppSelector } from '../../../../store/store';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const c = tokens.color;

/** The totals, plus the note on how this settlement is billed. */
const DockContent = ({ billingModality }: { billingModality?: string }): ReactElement => (
    <ThemeWrapper>
        <Stack gap={2}>
            <PoolTypography typography="subtitle1" translationId="app.common.overallOverview" />
            <BonusSettlementDetailSumTable />
            <PoolBanner variant="outlined">
                <FormattedMessage id="app.bonusSettlements.supplierDetail.banner" values={{ value: billingModality }} />
            </PoolBanner>
        </Stack>
    </ThemeWrapper>
);

/**
 * The chrome of the supplier's bonus settlement.
 *
 * The old header wrote its status label in white, which was legible only on the
 * legacy teal toolbar; on PALO's white one the word simply disappeared. The
 * header states the status as a pill now, so the question does not arise.
 */
const Chrome = ({ children }: { children: ReactElement }): ReactElement => {
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();
    const { id: bonusSettlementId } = useParams();
    const detail = useAppSelector(bonusSettlementDetailSelector);
    const loading = useAppSelector(bonusSettlementDetailLoadingSelector);
    const readOnly = useAppSelector(bonusSettlementDetailReadOnlySelector);
    const formikProps = useFormikContext<BonusSettlementDetailFormValues>();

    return (
        <PaloDetailView
            fillBody
            onBack={() => navigate(ROUTES.bonusSettlements.overview)}
            title={detail?.supplierName}
            status={
                <PaloStatusPill
                    status={detail?.status ?? BonusSettlementSupplierStatus.Open}
                    label={
                        <FormattedMessage
                            id={getBonusSettlementSupplierTranslationId(
                                detail?.status ?? BonusSettlementSupplierStatus.Open
                            )}
                        />
                    }
                />
            }
            actions={
                <>
                    {!readOnly && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<SaveIcon />}
                            disabled={loading}
                            onClick={() => {
                                if (bonusSettlementId) {
                                    dispatch(
                                        saveBonusSettlementDetail({
                                            bonusSettlementId: +bonusSettlementId,
                                            values: formikProps.values,
                                        })
                                    );
                                }
                                formikProps.resetForm({ values: formikProps.values });
                            }}
                        >
                            <FormattedMessage id="app.common.save" />
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        endIcon={<DownloadIcon />}
                        onClick={() =>
                            window.open(
                                urlJoin(API_BASE_URL, `/supplier-bonus-settlements/${bonusSettlementId}/export`),
                                '_self'
                            )
                        }
                    >
                        <FormattedMessage id="app.bonusSettlements.supplierDetail.export" />
                    </Button>
                </>
            }
            tabs={
                <Box sx={{ paddingBottom: '4px' }}>
                    <PaloFilterModal>
                        <BonusCustomerFilter />
                    </PaloFilterModal>
                </Box>
            }
            dock={
                <PaloSideRail
                    sections={[
                        {
                            key: 'details',
                            label: <FormattedMessage id="app.common.details" />,
                            content: <DockContent billingModality={detail?.billingModality} />,
                        },
                    ]}
                    width={420}
                    collapsedLabel={<FormattedMessage id="app.common.details" />}
                />
            }
        >
            {children}
        </PaloDetailView>
    );
};

export const PaloBonusSettlementDetail = (): ReactElement | null => {
    const dispatch = useThunkDispatch();
    const detail = useAppSelector(bonusSettlementDetailSelector);
    const { id: bonusSettlementId } = useParams();
    const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);

    useEffect(() => {
        if (bonusSettlementId) {
            dispatch(getBonusSettlementDetail(+bonusSettlementId));
        }

        return () => {
            dispatch(resetBonusSettlementDetail());
        };
    }, [bonusSettlementId, dispatch]);

    const handleSubmit = (
        values: BonusSettlementDetailFormValues,
        formikHelpers: FormikHelpers<BonusSettlementDetailFormValues>
    ): void => {
        if (bonusSettlementId) {
            dispatch(submitBonusSettlementDetail({ bonusSettlementId: +bonusSettlementId, values }));
        }

        formikHelpers.resetForm({ values });
    };

    const initialValues: BonusSettlementDetailFormValues = {
        customerBonusSettlements: detail?.customerBonusSettlements.map(settlement => ({ ...settlement })) ?? [],
        noteFromSupplier: detail?.noteFromSupplier ?? '',
    };

    if (!detail) {
        return null;
    }

    return (
        <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit}>
            <UrlFilter>
                <Chrome>
                    {/* The detail view hands its whole body to this page (fillBody):
                        a percentage height inside its shared scroll area resolved
                        to nothing, and the table drew its head over an empty body
                        (#2268, #2269). */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <OverviewTableContainer>
                                <ThemeWrapper>
                                    <Paper elevation={0} sx={{ height: '100%' }}>
                                        <BonusSettlementDetailTable />
                                    </Paper>
                                </ThemeWrapper>
                            </OverviewTableContainer>
                        </Box>

                        <Box sx={{ flexShrink: 0, borderTop: `1px solid ${c.line}` }}>
                            <ThemeWrapper>
                                <BonusSettlementDetailFooter bonusSettlementDetail={detail} />
                                <BonusSettlementDetailSubmitSuccessModal
                                    open={showSubmitSuccessModal}
                                    onClose={() => setShowSubmitSuccessModal(false)}
                                />
                            </ThemeWrapper>
                        </Box>
                    </Box>
                </Chrome>
            </UrlFilter>
        </Formik>
    );
};
